import React from "react";
import { NavLink, useHistory } from "react-router-dom";
import { useAccountantView } from "@/contexts";
import { LAB, Pill } from "@/components/ms";
import { cn } from "@/utils/shadcn";

interface AccountantBannerProps {
  clientName: string;
  onBack?: () => void;
}

/**
 * Client-view chrome: shown while an accountant is inside a client's account.
 * It carries navigation, so it is a persistent mode strip rather than one of the
 * screen's alerts — it never competes for the single AlertBar slot.
 */
export const AccountantBanner: React.FC<AccountantBannerProps> = ({ clientName, onBack }) => {
  const history = useHistory();
  const { exitClientView } = useAccountantView();

  const handleExit = () => {
    exitClientView();

    if (onBack) {
      onBack();
    } else {
      history.push("/a/clients");
    }
  };

  const navLinks = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/transactions", label: "Transactions" },
    { to: "/audit", label: "Audit" },
    { to: "/tax", label: "Tax" },
  ];

  return (
    <div className="sticky top-0 z-50 border-y border-ms-line bg-ms-tan-soft px-[var(--pad)] py-3 print:hidden">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-[13px]">
            <span className="h-[7px] w-[7px] flex-none rounded-full bg-ms-tan" />
            <span className="truncate text-[13px] leading-[1.45] text-ms-mute">
              <b className="font-semibold text-ms-ink">Viewing {clientName}</b> — read-only.
            </span>
          </span>
          <Pill onClick={handleExit} className="flex-none">
            Exit client view
          </Pill>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={cn(
                LAB,
                "whitespace-nowrap px-2 py-1 text-[9.5px] text-ms-mute transition-colors hover:text-ms-ink"
              )}
              activeClassName="text-ms-ink"
            >
              {link.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};
